import { toast } from "react-toastify";
import { useSelector, useStore } from "react-redux";
import { useNavigate } from "react-router-dom";
import useApiInterceptors from "@wine2trace/auth/useApiInterceptors";
import useRefreshToken from "../hooks/useRefreshToken";
import { axiosPrivate } from "../api/axios";
import { selectActiveWinery } from "../context/selectors";
import { getItem } from "../services/LocalStorage";
import useEndSession from "../hooks/useEndSession";
import useExitImpersonation from "../hooks/useExitImpersonation";

// Interceptors of the private client (@wine2trace/auth), mounted once in index.js, with the webapp
// parts: active winery headers, impersonation expiry, toast on denied reads, wineries list on 428.
// No UI. Components get the client with useAxiosPrivate.
const ApiInterceptors = () => {
  const refresh = useRefreshToken();
  const endSession = useEndSession();
  const exitImpersonation = useExitImpersonation();
  const navigate = useNavigate();
  const store = useStore();
  const activeWinery = useSelector(selectActiveWinery);

  const getRequestHeaders = () =>
    activeWinery
      ? {
          clientname: activeWinery?.client_name || "",
          wineryid: String(activeWinery?.id) || "",
        }
      : {};

  // 🕵️ Sessione di impersonazione scaduta: non esiste un refresh
  // dedicato per il token impersonato (breve, 15 min, per design).
  // Si torna alla sessione admin originale invece di tentare il
  // refresh normale, che risolverebbe erroneamente come il target.
  // Lo stato di impersonazione si legge dallo store al momento dell'errore.
  const onTokenExpired = async (error) => {
    if (!store.getState().impersonation.active) return false;
    toast.info(
      getItem("i18LanguageLocale")?.split("_")?.[0] === "en"
        ? "Impersonation session expired"
        : "Sessione di impersonazione scaduta",
      { position: toast.POSITION.BOTTOM_RIGHT }
    );
    error.impersonationHandled = true;
    // ripristino locale dell'admin (o refresh/fine sessione): mai a meta'
    await exitImpersonation();
    return true;
  };

  // Le mutazioni passano da toastAxios, che mostra già il proprio toast sul 403.
  // Messaggio statico: useLabels non è utilizzabile qui (dipende a sua
  // volta da useAxiosPrivate → ricorsione infinita di hook).
  const onForbiddenRead = (error) => {
    const data = error?.response?.data;
    const reqId = data?.reqId || error?.response?.headers?.["x-request-id"];
    const resource = data?.details?.resource;
    const action = data?.details?.action;
    const lang = getItem("i18LanguageLocale")?.split("_")?.[0] || "it";
    const base = lang === "en" ? "Access denied" : "Accesso negato";
    const detail = resource ? `: ${resource} (${action})` : "";
    const ref = reqId ? ` [${reqId}]` : "";
    toast.error(`${base}${detail}${ref}`, {
      toastId: `forbidden-read-${resource || "unknown"}`,
      position: toast.POSITION.BOTTOM_RIGHT,
      autoClose: reqId ? 8000 : 4000,
    });
  };

  const onWineryRequired = () => {
    const activeWineryId = getItem("activeWineryId");
    if (!activeWineryId) {
      navigate("/#wineriesList");
    }
  };

  useApiInterceptors({
    apiPrivate: axiosPrivate,
    refresh,
    endSession,
    getRequestHeaders,
    onTokenExpired,
    onForbiddenRead,
    onWineryRequired,
  });

  return null;
};

export default ApiInterceptors;
